import { DayCell } from './DayCell';
import type { CalendarMonth, CalendarDay } from '../../hooks/useCalendarData';
import type { WageType, AbsenceType } from '../../types';
import type { VacationRecalcEntry } from '../../utils/calculations';

interface MonthViewProps {
  month: CalendarMonth;
  focusedDate?: string | null;
  onDayClick: (day: CalendarDay) => void;
  onDayDoubleClick?: (day: CalendarDay) => void;
  /** WageType des betrachteten Users — wird an DayCell weitergereicht. */
  viewedUserWageType?: WageType | null;
  /** Overlay mit dynamisch berechneten Urlaubsstunden für zukünftige Tage */
  vacationRecalcMap?: Map<string, VacationRecalcEntry>;
  /** AbsenceType-Lookup nach ID — wird an DayCell weitergereicht */
  absenceTypeById?: (id: string) => AbsenceType | undefined;
  /** Map von dateString → vorgemerkter Abwesenheit (PLANNED-Overlay) */
  plannedAbsenceMap?: Map<string, { absenceType?: AbsenceType; requestId: string }>;
}

const WEEKDAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WEEKDAY_FULL = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

export function MonthView({ month, focusedDate, onDayClick, onDayDoubleClick, viewedUserWageType, vacationRecalcMap, absenceTypeById, plannedAbsenceMap }: MonthViewProps) {
  // Erstelle leere Zellen für den Offset am Anfang des Monats
  const emptyCells = Array.from({ length: month.startOffset }, (_, i) => (
    <div key={`empty-${i}`} className="aspect-square" />
  ));

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 mb-3 text-center">
        {month.name}
      </h3>

      {/* Wochentage Header */}
      <div className="grid grid-cols-7 gap-1 mb-2" role="row">
        {WEEKDAY_NAMES.map((name, i) => (
          <div
            key={name}
            role="columnheader"
            aria-label={WEEKDAY_FULL[i]}
            className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase py-1"
          >
            {name}
          </div>
        ))}
      </div>

      {/* Tage Grid */}
      <div className="grid grid-cols-7 gap-1" role="grid" aria-label={month.name}>
        {emptyCells}
        {month.days.map((day) => {
          const recalcEntry = vacationRecalcMap?.get(day.dateString);
          const plannedAbsence = plannedAbsenceMap?.get(day.dateString);
          return (
            <DayCell
              key={day.dateString}
              day={day}
              isFocused={focusedDate === day.dateString}
              onClick={onDayClick}
              onDoubleClick={onDayDoubleClick}
              viewedUserWageType={viewedUserWageType}
              isProvisional={recalcEntry?.isProvisional}
              provisionalHours={recalcEntry ? recalcEntry.hours : undefined}
              absenceTypeById={absenceTypeById}
              plannedAbsence={plannedAbsence}
            />
          );
        })}
      </div>
    </div>
  );
}
